import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  googleMapsSearchUrlFromQuery,
  googleMapsUrlFromCoordinates
} from '../../maps/google-maps-url';

export type GoogleMapsLinkVariant = 'pill' | 'compact' | 'inline';

@Component({
  selector: 'app-google-maps-link',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './google-maps-link.component.html',
  styleUrl: './google-maps-link.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleMapsLinkComponent {
  /** Pre-built href; takes precedence over {@link query} and coordinates. */
  readonly url = input<string | null>(null);
  readonly query = input<string | null>(null);
  readonly latitude = input<number | string | null>(null);
  readonly longitude = input<number | string | null>(null);
  readonly label = input('Open in Google Maps');
  /**
   * - ``pill`` — bordered chip (HITL address blocks).
   * - ``compact`` — small pin + external icon (lists).
   * - ``inline`` — text link beside coordinates (station detail).
   */
  readonly variant = input<GoogleMapsLinkVariant>('pill');

  protected readonly href = computed(() => {
    const direct = this.url()?.trim();
    if (direct) {
      return direct;
    }
    const lat = coerceCoordinate(this.latitude());
    const lng = coerceCoordinate(this.longitude());
    if (lat != null && lng != null) {
      return googleMapsUrlFromCoordinates(lat, lng);
    }
    const q = this.query()?.trim();
    if (q) {
      return googleMapsSearchUrlFromQuery(q);
    }
    return null;
  });
}

function coerceCoordinate(value: number | string | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}
